import React from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, ClipboardCheck, Award, Target, Sparkles, TrendingUp, Calendar,
  AlertCircle, BookOpen,
} from "lucide-react";
import { YEAR_LABEL, TRACK_LABEL } from "@/lib/school";
import { fmtDate } from "@/lib/format";

const GRADE_COLORS = {
  "18-21": "#94a3b8",
  "22-24": "#0891b2",
  "25-27": "#0ea5e9",
  "28-30": "#16a34a",
  "30L":  "#E2725B",
  "idoneo": "#7C3AED",
};

export default function StudentStats() {
  const { id } = useParams();
  const listBase = useLocation().pathname.startsWith("/modulare") ? "/modulare/studenti" : "/scuola/studenti";
  const { data, isLoading } = useQuery({
    queryKey: ["student-stats", id],
    queryFn: async () => (await api.get(`/school-stats/students/${id}`)).data,
  });

  if (isLoading || !data) {
    return <div className="p-10 text-center text-sm text-stone-400">Caricamento…</div>;
  }
  const { student, attendance, grades, cfu, upcoming_bookings } = data;

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <div className="flex items-center gap-3 mb-5 text-sm">
        <Link to={`${listBase}/${id}`} className="text-stone-500 hover:text-[#1C352D] flex items-center gap-1">
          <ArrowLeft size={14}/> Scheda studente
        </Link>
      </div>

      <PageHeader
        title={`Statistiche · ${student.first_name} ${student.last_name}`}
        subtitle={
          <span className="text-stone-500">
            Matricola <b className="text-[#1C352D]">{student.matricola}</b> ·{" "}
            {YEAR_LABEL[student.year_level] || `${student.year_level}° anno`} ·{" "}
            <span className="capitalize">{TRACK_LABEL[student.track_type] || student.track_type}</span>
            {student.academic_year && <> · A.A. {student.academic_year}</>}
          </span>
        }
      />

      {/* Progress diploma + anno corrente */}
      <Card className="p-6 border border-stone-200 rounded-xl shadow-none bg-gradient-to-br from-[#1C352D] to-[#264A3E] text-white mb-6">
        <div className="flex items-center gap-6">
          <div className="relative w-32 h-32 shrink-0">
            <CircleProgress value={cfu.year?.target ? cfu.year.pct : cfu.progress_pct}/>
            <div className="absolute inset-0 grid place-items-center text-center">
              <div>
                <div className="text-3xl font-bold" data-testid="stat-cfu-pct">
                  {(cfu.year?.target ? cfu.year.pct : cfu.progress_pct).toFixed(0)}%
                </div>
                <div className="text-[10px] opacity-70 uppercase tracking-widest">
                  {cfu.year?.target ? `${cfu.year.level}° anno` : "diploma"}
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1">
            <div className="text-lg font-medium flex items-center gap-2"><Target size={18}/> Progresso CFU</div>
            {cfu.year?.target > 0 ? (
              <>
                <div className="text-3xl font-semibold mt-1" data-testid="stat-total-cfu">
                  {cfu.year.earned}<span className="text-base opacity-70"> / {cfu.year.target} CFU {cfu.year.level}° anno</span>
                </div>
                <div className="text-xs opacity-80 mt-2">
                  Diploma totale: <b>{cfu.total}/{cfu.target} CFU</b> da corsi · {cfu.from_exams} esami superati · {cfu.progress_pct.toFixed(0)}%
                  {cfu.practical > 0 && <span> · <b>{cfu.practical}</b> crediti teologia pratica <i>(fuori conteggio diploma)</i></span>}
                </div>
              </>
            ) : (
              <>
                <div className="text-3xl font-semibold mt-1" data-testid="stat-total-cfu">
                  {cfu.total}<span className="text-base opacity-70"> / {cfu.target} CFU diploma</span>
                </div>
                <div className="text-xs opacity-80 mt-2">
                  {cfu.from_exams} CFU da esami · {Math.max(0, cfu.target - cfu.total)} mancanti al diploma
                </div>
              </>
            )}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <MiniKpi icon={ClipboardCheck} label="Presenze totali" value={`${attendance.rate.toFixed(0)}%`} sub={`${attendance.total} rilevazioni`} testId="stat-attendance-rate"/>
        <MiniKpi icon={TrendingUp} label="Ultimi 30gg" value={`${attendance.recent_30d.rate.toFixed(0)}%`} sub={`${attendance.recent_30d.total} lezioni`} testId="stat-attendance-30d"/>
        <MiniKpi icon={Award} label="Media aritmetica" value={grades.media_aritmetica ?? "—"} sub={`${grades.passed} esami sup.`} testId="stat-media-arit"/>
        <MiniKpi icon={Sparkles} label="Media ponderata" value={grades.media_ponderata ?? "—"} sub="CFU pesati" testId="stat-media-pond"/>
        <MiniKpi icon={BookOpen} label="CFU teologia pratica" value={cfu.practical_year?.cfu ?? 0} sub={`A.A. ${cfu.practical_year?.academic_year || ""} · ${cfu.practical_year?.activities ?? 0} attività`} testId="stat-practical-year"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Presenze breakdown */}
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-sm font-medium flex items-center gap-2 mb-4"><ClipboardCheck size={14}/> Presenze</div>
          <div className="space-y-2.5">
            <PresenceBar label="Presente"    val={attendance.counts.presente}     total={attendance.total} color="#16a34a"/>
            <PresenceBar label="Ritardo"     val={attendance.counts.ritardo}      total={attendance.total} color="#f59e0b"/>
            <PresenceBar label="Giustificato" val={attendance.counts.giustificato} total={attendance.total} color="#0891b2"/>
            <PresenceBar label="Assente"      val={attendance.counts.assente}      total={attendance.total} color="#dc2626"/>
          </div>
          {attendance.rate < 70 && attendance.total >= 5 && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800 flex items-start gap-2">
              <AlertCircle size={14} className="shrink-0 mt-0.5"/>
              Presenza sotto la soglia consigliata (70%). Verificare eventuali giustificativi mancanti.
            </div>
          )}
        </Card>

        {/* Distribuzione voti */}
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-sm font-medium flex items-center gap-2 mb-4"><Award size={14}/> Distribuzione voti</div>
          <GradeDistribution dist={grades.distribution}/>
          <div className="mt-4 pt-3 border-t border-stone-100 text-xs text-stone-600 space-y-1">
            <div>Esami superati: <b>{grades.passed}</b></div>
            <div>Esami non superati: <b className="text-red-700">{grades.failed}</b></div>
            <div>Totale registrati: <b>{grades.total_recorded}</b></div>
          </div>
        </Card>

        {/* CFU per area */}
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-sm font-medium flex items-center gap-2 mb-4"><BookOpen size={14}/> CFU per area</div>
          {cfu.by_area.length === 0 ? (
            <div className="text-xs text-stone-400 text-center py-6">Nessun credito ancora maturato.</div>
          ) : (
            <div className="space-y-2.5">
              {cfu.by_area.slice(0, 8).map((a, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="truncate flex-1">{a.area_name}</span>
                    <span className="font-medium ml-2">{a.cfu} CFU · {a.exams} es.</span>
                  </div>
                  <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.min((a.cfu / (cfu.target/8)) * 100, 100)}%`, background: a.color || "#1C352D" }}/>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Presenze per corso */}
      {((attendance.by_course && attendance.by_course.length > 0) || (attendance.by_seminar && attendance.by_seminar.length > 0)) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {attendance.by_course && attendance.by_course.length > 0 && (
            <Card className="border border-stone-200 rounded-xl shadow-none" data-testid="attendance-by-course">
              <div className="p-5 border-b border-stone-100 text-sm font-medium flex items-center gap-2">
                <BookOpen size={14}/> Presenze per corso
              </div>
              <ul className="divide-y divide-stone-100">
                {attendance.by_course.map((c) => (
                  <li key={c.course_id} className="p-4" data-testid={`att-course-${c.course_id}`}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="font-medium truncate">{c.course_name}</span>
                      <span className="text-xs text-stone-600"><b>{c.attended}</b> / {c.total} lezioni</span>
                    </div>
                    <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${c.rate}%`, background: c.rate >= 70 ? "#16a34a" : c.rate >= 50 ? "#f59e0b" : "#dc2626" }}/>
                    </div>
                    <div className="text-[10px] text-stone-500 mt-1 flex gap-2">
                      <span>{c.rate}%</span>
                      {c.counts?.assente > 0 && <span className="text-red-700">{c.counts.assente} assenze</span>}
                      {c.counts?.ritardo > 0 && <span className="text-orange-700">{c.counts.ritardo} ritardi</span>}
                      {c.counts?.giustificato > 0 && <span className="text-stone-500">{c.counts.giustificato} giustif.</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {attendance.by_seminar && attendance.by_seminar.length > 0 && (
            <Card className="border border-stone-200 rounded-xl shadow-none" data-testid="attendance-by-seminar">
              <div className="p-5 border-b border-stone-100 text-sm font-medium flex items-center gap-2">
                <Sparkles size={14}/> Presenze per seminario
              </div>
              <ul className="divide-y divide-stone-100">
                {attendance.by_seminar.map((c) => (
                  <li key={c.course_id} className="p-4">
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="font-medium truncate">{c.course_name}</span>
                      <span className="text-xs text-stone-600"><b>{c.attended}</b> / {c.total} lezioni</span>
                    </div>
                    <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${c.rate}%`, background: "#E2725B" }}/>
                    </div>
                    <div className="text-[10px] text-stone-500 mt-1">{c.rate}%</div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Ultimi voti */}
        <Card className="border border-stone-200 rounded-xl shadow-none">
          <div className="p-5 border-b border-stone-100">
            <div className="text-sm font-medium flex items-center gap-2"><Award size={14}/> Ultimi voti registrati</div>
          </div>
          {grades.recent.length === 0 ? (
            <div className="p-8 text-sm text-stone-400 text-center">Nessun voto registrato.</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {grades.recent.map((g) => (
                <li key={g.id} data-testid={`stat-grade-${g.id}`} className="p-3 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg grid place-items-center text-white text-xs font-bold ${g.is_passed ? "bg-[#1C352D]" : "bg-red-700"}`}>
                    {g.grade_value === "idoneo" ? "OK" : g.grade_value === "non_idoneo" ? "KO" : g.grade_value}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{g.course_name || "Corso"}</div>
                    <div className="text-[11px] text-stone-500">{fmtDate(g.date)} · {g.cfu || 0} CFU</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Prossimi appelli */}
        <Card className="border border-stone-200 rounded-xl shadow-none">
          <div className="p-5 border-b border-stone-100">
            <div className="text-sm font-medium flex items-center gap-2"><Calendar size={14}/> Appelli prenotati</div>
          </div>
          {upcoming_bookings.length === 0 ? (
            <div className="p-8 text-sm text-stone-400 text-center">Nessuna prenotazione futura.</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {upcoming_bookings.map((b) => (
                <li key={b.id} data-testid={`stat-booking-${b.id}`}>
                  <Link to={`/scuola/esami/${b.id}`} className="p-3 flex items-center gap-3 hover:bg-stone-50/60">
                    <div className="w-12 h-12 rounded-lg bg-[#FDFBF7] grid place-items-center border border-stone-200">
                      <div className="text-lg font-semibold text-[#1C352D]">{parseInt(b.date?.slice(8,10) || "0", 10)}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{b.course_name || "Corso"}</div>
                      <div className="text-[11px] text-stone-500">{fmtDate(b.date)} · {b.time?.slice(0,5) || "orario da definire"}</div>
                    </div>
                    <Badge className={`text-[10px] rounded-full ${b.status === "aperto" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-stone-100 text-stone-700"}`}>{b.status}</Badge>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function MiniKpi({ icon: Icon, label, value, sub, testId }) {
  return (
    <Card className="p-4 border border-stone-200 rounded-xl shadow-none" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
        <Icon size={11}/> {label}
      </div>
      <div className="text-2xl font-semibold mt-1 text-[#1C352D]">{value}</div>
      {sub && <div className="text-[10px] text-stone-500 mt-0.5">{sub}</div>}
    </Card>
  );
}

function PresenceBar({ label, val, total, color }) {
  const pct = total ? (val / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-stone-700">{label}</span>
        <span className="text-stone-500">{val} · {pct.toFixed(0)}%</span>
      </div>
      <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }}/>
      </div>
    </div>
  );
}

function GradeDistribution({ dist }) {
  const entries = Object.entries(dist).filter(([, v]) => v > 0);
  const max = Math.max(1, ...Object.values(dist));
  if (entries.length === 0) return <div className="text-xs text-stone-400 text-center py-6">Nessun voto ancora registrato.</div>;
  return (
    <div className="flex items-end gap-2 h-32">
      {Object.entries(dist).map(([k, v]) => {
        const h = max ? (v / max) * 100 : 0;
        return (
          <div key={k} className="flex-1 flex flex-col items-center justify-end gap-1 min-w-0">
            <div className="text-[10px] text-stone-600">{v || ""}</div>
            <div
              className="w-full rounded-md transition-all"
              style={{ height: `${Math.max(h, v > 0 ? 6 : 2)}%`, background: v > 0 ? GRADE_COLORS[k] : "#f5f5f4" }}
            />
            <div className="text-[9px] uppercase tracking-widest text-stone-500 truncate w-full text-center">{k}</div>
          </div>
        );
      })}
    </div>
  );
}

function CircleProgress({ value }) {
  const size = 128;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.15)" strokeWidth={stroke} fill="none"/>
      <circle
        cx={size / 2} cy={size / 2} r={r}
        stroke="#E2725B" strokeWidth={stroke} fill="none"
        strokeDasharray={c} strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 800ms ease" }}
      />
    </svg>
  );
}
